import {
  ChevronRight,
  MapPin,
  Code,
  Globe,
  Smartphone,
  Circle,
} from "lucide-react";
import CTASection from "../../components/cta-section";
import { useTranslation } from "../../i18n";

export default function NewMap() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen overflow-x-hidden relative">
      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pt-64 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-navy-900 leading-tight mb-6">
              {t('routes.map.heroTitle')}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              {t('routes.map.heroDescription')}
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
              <div className="flex flex-col items-center text-center">
                <div className="h-12 w-12 flex-shrink-0 mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('routes.map.features.realtimeTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('routes.map.features.realtimeDesc')}
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="h-12 w-12 flex-shrink-0 mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <Code className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('routes.map.features.integrationTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('routes.map.features.integrationDesc')}
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="h-12 w-12 flex-shrink-0 mb-3 rounded-full bg-purple-100 flex items-center justify-center">
                  <Globe className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('routes.map.features.globalTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('routes.map.features.globalDesc')}
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="h-12 w-12 flex-shrink-0 mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('routes.map.features.mobileTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('routes.map.features.mobileDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Full Width Map Section */}
      <section id="map" className="w-full bg-white pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Circle
                className="w-3 h-3 mr-2 text-red-500"
                fill="currentColor"
                stroke="none"
              />
              {t('routes.map.liveDemoBadge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('routes.map.liveMapTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('routes.map.liveMapDescription')}
            </p>
          </div>
        </div>

        {/* Full Width Iframe Container */}
        <div className="w-full">
          <div className="relative w-full" style={{ height: "600px" }}>
            <iframe
              src="https://app.juhuu.app/"
              className="w-full h-full border-0"
              title={t('routes.map.iframeTitle')}
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>

        {/* Integration Info */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('routes.map.builtForOperatorsTitle')}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {t('routes.map.builtForOperatorsDescription')}
              </p>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
                    <ChevronRight className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {t('routes.map.bulletPoints.locationsTitle')}
                    </h4>
                    <p className="text-gray-600">
                      {t('routes.map.bulletPoints.locationsDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
                    <ChevronRight className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {t('routes.map.bulletPoints.bookingTitle')}
                    </h4>
                    <p className="text-gray-600">
                      {t('routes.map.bulletPoints.bookingDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
                    <ChevronRight className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {t('routes.map.bulletPoints.brandingTitle')}
                    </h4>
                    <p className="text-gray-600">
                      {t('routes.map.bulletPoints.brandingDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
                    <ChevronRight className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {t('routes.map.bulletPoints.operatorTitle')}
                    </h4>
                    <p className="text-gray-600">
                      {t('routes.map.bulletPoints.operatorDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">
                {t('routes.map.integrationTitle')}
              </h4>
              <div className="bg-gray-800 rounded-lg p-4 overflow-x-auto">
                <code className="text-green-400 text-sm">
                  {`<iframe 
  src="https://app.juhuu.app/"
  width="100%" 
  height="600"
  frameborder="0">
</iframe>`}
                </code>
              </div>
              <p className="text-gray-300 text-sm mt-4">
                {t('routes.map.integrationDescription')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
}
